import LegalLayout from './LegalLayout';

export default function HipaaCompliancePage() {
  return (
    <LegalLayout title="HIPAA Compliance" effectiveDate="May 2, 2026" lastUpdated="May 2, 2026" policyKey="hipaa">
      <p>
        Plexura AI Scribe (&ldquo;<strong>Plexura</strong>&rdquo;) provides clinical
        documentation services to licensed mental and behavioral health
        professionals. When our customers (&ldquo;<strong>Covered Entities</strong>&rdquo;
        as defined under HIPAA) use the Service to create, receive,
        maintain, or transmit Protected Health Information (&ldquo;<strong>PHI</strong>&rdquo;)
        on behalf of their patients, Plexura acts as a{' '}
        <strong>HIPAA Business Associate</strong> within the meaning of
        45 C.F.R. &sect; 160.103.
      </p>
      <p>
        This HIPAA Compliance Statement describes the administrative,
        physical, and technical safeguards Plexura has implemented to
        comply with the HIPAA Privacy Rule (45 C.F.R. Part 164, Subpart
        E), the HIPAA Security Rule (Subpart C), the Breach Notification
        Rule (Subpart D), and the HITECH Act amendments thereto. It is
        published in addition to, and not in lieu of, the Business
        Associate Agreement (&ldquo;<strong>BAA</strong>&rdquo;) signed between Plexura and
        each Covered Entity that uses the Service to handle PHI. In the
        event of a conflict, the executed BAA controls.
      </p>

      <h2>1. Definitions</h2>
      <p>
        Capitalized terms not defined here have the meaning given in 45
        C.F.R. Parts 160 and 164. &ldquo;<strong>PHI</strong>&rdquo; means individually
        identifiable health information transmitted or maintained in any
        form. &ldquo;<strong>ePHI</strong>&rdquo; means electronic PHI. &ldquo;<strong>Workforce</strong>&rdquo; means
        employees, contractors, and volunteers under the direct control
        of Plexura.
      </p>

      <h2>2. Business Associate Status and BAA Requirement</h2>
      <p>
        Plexura will only create, receive, maintain, or transmit PHI on
        behalf of a Covered Entity that has executed a current BAA with
        Plexura. The BAA sets out:
      </p>
      <ul>
        <li>
          The permitted and required uses and disclosures of PHI by
          Plexura, limited to what is necessary to perform the Service or
          as required by law;
        </li>
        <li>
          Restrictions on disclosing PHI to subcontractors without a
          written BAA flow-down;
        </li>
        <li>
          Obligations to safeguard PHI in accordance with the Security
          Rule;
        </li>
        <li>
          Reporting and breach-notification obligations;
        </li>
        <li>
          Termination, return, and destruction provisions; and
        </li>
        <li>
          The Covered Entity&rsquo;s right to audit Plexura&rsquo;s
          compliance.
        </li>
      </ul>
      <p>
        A copy of the standard Plexura BAA is available on request from{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>.
      </p>

      <h2>3. Administrative Safeguards (45 C.F.R. &sect; 164.308)</h2>
      <h3>3.1 Security Management Process &mdash; &sect; 164.308(a)(1)</h3>
      <p>
        Plexura maintains a written information-security program that
        includes annual risk analysis, documented risk-management
        decisions, sanctions policy for workforce non-compliance, and a
        security-incident review log.
      </p>
      <h3>3.2 Assigned Security Responsibility &mdash; &sect; 164.308(a)(2)</h3>
      <p>
        Plexura has appointed a designated Privacy &amp; Security Officer
        responsible for the development and implementation of the
        policies and procedures required by the HIPAA Rules. The Privacy
        &amp; Security Officer can be reached at{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>.
      </p>
      <h3>3.3 Workforce Security &mdash; &sect; 164.308(a)(3)</h3>
      <ul>
        <li>
          All workforce members complete HIPAA Privacy and Security
          training upon hire and annually thereafter.
        </li>
        <li>
          Access to PHI is provisioned on the principle of least
          privilege and is reviewed at least quarterly.
        </li>
        <li>
          Access is revoked within 24 hours of role change or
          separation.
        </li>
        <li>
          Background checks are performed for workforce members with
          PHI access, where permitted by local law.
        </li>
      </ul>
      <h3>3.4 Information Access Management &mdash; &sect; 164.308(a)(4)</h3>
      <ul>
        <li>
          Role-based access control (RBAC) is enforced at the practice
          and account level.
        </li>
        <li>
          Multi-tenant isolation: a clinician&rsquo;s account cannot
          access another customer&rsquo;s PHI under any circumstance.
        </li>
        <li>
          Within a multi-clinician practice, owners can configure which
          members may view, edit, push, or export which patient
          records.
        </li>
      </ul>
      <h3>3.5 Security Awareness and Training &mdash; &sect; 164.308(a)(5)</h3>
      <p>
        Workforce members receive ongoing security reminders, password
        guidance, malicious-software protection training, log-in
        monitoring expectations, and password-management training. Access
        is rate-limited and brute-force protected; repeated failed
        log-ins trigger investigation.
      </p>
      <h3>3.6 Security Incident Procedures &mdash; &sect; 164.308(a)(6)</h3>
      <p>
        Plexura maintains a documented incident-response plan. Suspected
        incidents are triaged within one business hour, escalated to the
        Privacy &amp; Security Officer, and tracked through resolution.
        Customers are notified per Section&nbsp;9 below.
      </p>
      <h3>3.7 Contingency Plan &mdash; &sect; 164.308(a)(7)</h3>
      <ul>
        <li>
          <strong>Data backup:</strong> automated, encrypted backups of
          PHI databases on a continuous-replication and daily-snapshot
          schedule, retained per the BAA.
        </li>
        <li>
          <strong>Disaster recovery:</strong> documented runbooks,
          recovery-point objective (RPO) of 24 hours and recovery-time
          objective (RTO) of 8 hours for critical services.
        </li>
        <li>
          <strong>Emergency-mode operation:</strong> read-only PHI
          access can be restored from backup if primary infrastructure
          is unavailable.
        </li>
        <li>
          <strong>Testing:</strong> recovery procedures are tested at
          least annually.
        </li>
      </ul>
      <h3>3.8 Evaluation &mdash; &sect; 164.308(a)(8)</h3>
      <p>
        Plexura performs technical and non-technical evaluations of its
        environment at least annually, plus event-driven evaluations
        when material operational or environmental changes occur.
      </p>
      <h3>3.9 Business Associate Contracts with Subcontractors &mdash; &sect; 164.308(b)</h3>
      <p>
        Plexura executes a written BAA with every subcontractor that
        creates, receives, maintains, or transmits PHI on
        Plexura&rsquo;s behalf, including:
      </p>
      <ul>
        <li>MongoDB, Inc. (Atlas database hosting)</li>
        <li>OpenAI, L.L.C. (speech-to-text and LLM inference)</li>
        <li>Resend, Inc. (transactional email delivery)</li>
        <li>Stripe, Inc. (payment processing &mdash; non-PHI billing data only)</li>
        <li>Emergent Cloud / underlying Kubernetes infrastructure provider</li>
      </ul>

      <h2>4. Physical Safeguards (45 C.F.R. &sect; 164.310)</h2>
      <p>
        Plexura does not operate its own data centers. PHI is stored
        exclusively in cloud-provider data centers (MongoDB Atlas, the
        managed Kubernetes provider) located in the United States. Each
        provider maintains:
      </p>
      <ul>
        <li>SOC 2 Type II audited physical-security controls;</li>
        <li>24x7 staffed access control with multi-factor authentication;</li>
        <li>Biometric or token-based facility entry;</li>
        <li>Continuous video surveillance and intrusion detection;</li>
        <li>
          Workstation use, security, and disposal controls per &sect;
          164.310(b)&ndash;(d), enforced by Plexura for all workforce devices.
        </li>
      </ul>
      <p>
        Workforce devices used to access PHI must run a current operating
        system, full-disk encryption, an active screen-lock policy, and
        endpoint-protection software.
      </p>

      <h2>5. Technical Safeguards (45 C.F.R. &sect; 164.312)</h2>
      <h3>5.1 Access Control &mdash; &sect; 164.312(a)</h3>
      <ul>
        <li>
          Unique user IDs for every account; passwords hashed with
          bcrypt and a random per-user salt.
        </li>
        <li>
          Password policy: minimum 12 characters, three of four
          character classes, breach-pattern blocklist, sequence/repeat
          rejection.
        </li>
        <li>
          Automatic logoff via signed JWTs that expire after 120 minutes
          of issuance.
        </li>
        <li>
          Encryption and decryption of ePHI: TLS 1.2+ in transit;
          AES-256 at rest in the database, in object storage, and in
          backups.
        </li>
        <li>
          Optional emergency-access procedure for designated workforce
          members during a declared incident.
        </li>
      </ul>
      <h3>5.2 Audit Controls &mdash; &sect; 164.312(b)</h3>
      <p>
        Plexura maintains immutable audit logs of every login, PHI
        access, modification, deletion, export, and EHR-push event.
        Logs include actor identity, timestamp, source IP, user-agent,
        action, target resource, and result. Logs are retained for at
        least six (6) years per &sect; 164.316(b)(2)(i) and surfaced to
        Covered Entities through the Plexura analytics dashboard for
        their own compliance reporting.
      </p>
      <h3>5.3 Integrity &mdash; &sect; 164.312(c)</h3>
      <p>
        ePHI is protected from improper alteration through database-level
        write controls, version history on transcripts and notes, and
        cryptographic checksums during backup and restore. The audit log
        provides a forensic trail to detect any unauthorized alteration.
      </p>
      <h3>5.4 Person or Entity Authentication &mdash; &sect; 164.312(d)</h3>
      <p>
        Plexura authenticates every API request via short-lived signed
        JWT tokens. Browser-extension push payloads are signed and
        validated server-side before any action is taken.
        Multi-factor authentication is available for owner and
        administrator accounts and is on the product roadmap for all
        accounts.
      </p>
      <h3>5.5 Transmission Security &mdash; &sect; 164.312(e)</h3>
      <ul>
        <li>
          All client-server communication is encrypted with TLS 1.2 or
          higher, with weak ciphers disabled.
        </li>
        <li>
          HTTP Strict Transport Security (HSTS), X-Frame-Options:
          DENY, X-Content-Type-Options: nosniff, and a strict
          Referrer-Policy are enforced on every response.
        </li>
        <li>
          A least-privilege CORS allowlist prevents unauthorized
          cross-origin credentialed requests.
        </li>
        <li>
          Inter-service traffic between application pods and the
          database is encrypted within the provider&rsquo;s private
          network.
        </li>
      </ul>

      <h2>6. Privacy Rule Implementation (45 C.F.R. Part 164, Subpart E)</h2>
      <h3>6.1 Permitted Uses and Disclosures</h3>
      <p>
        Plexura uses and discloses PHI only to:
      </p>
      <ul>
        <li>Provide the Service to the Covered Entity;</li>
        <li>Carry out Plexura&rsquo;s management and administration, where permitted by 45 C.F.R. &sect; 164.504(e)(4);</li>
        <li>Carry out Plexura&rsquo;s legal responsibilities;</li>
        <li>Disclose PHI as required by law (e.g., subpoena);</li>
        <li>Provide data aggregation services to the Covered Entity, where requested.</li>
      </ul>
      <p>
        Plexura applies the <strong>minimum-necessary</strong> standard
        to all uses and disclosures of PHI.
      </p>
      <h3>6.2 Individual Rights</h3>
      <p>
        Where a patient (the &ldquo;individual&rdquo; under HIPAA) submits
        a rights request directly to Plexura, we will redirect the
        request to the Covered Entity, who is responsible for
        fulfillment, and will provide reasonable cooperation, including:
      </p>
      <ul>
        <li>
          Right of access (&sect; 164.524) &mdash; export and delivery of
          the patient&rsquo;s designated record set.
        </li>
        <li>
          Right of amendment (&sect; 164.526) &mdash; ability to update
          PHI within the Service at the Covered Entity&rsquo;s
          direction.
        </li>
        <li>
          Right to an accounting of disclosures (&sect; 164.528) &mdash;
          via the audit-log export feature.
        </li>
        <li>
          Right to restrict (&sect; 164.522) &mdash; honored when the
          Covered Entity instructs Plexura accordingly.
        </li>
      </ul>
      <h3>6.3 De-Identification</h3>
      <p>
        Where Plexura uses datasets internally for service improvement,
        de-identification is performed in accordance with the Safe Harbor
        method (&sect; 164.514(b)(2)) or expert determination
        (&sect; 164.514(b)(1)), as applicable. We do not use PHI to train
        public AI models.
      </p>

      <h2>7. Sanctions and Workforce Discipline</h2>
      <p>
        Workforce members who violate these policies are subject to
        progressive discipline up to and including termination of
        employment and, where applicable, civil and criminal referral.
        Sanctions actions are documented and retained per &sect;
        164.530(e).
      </p>

      <h2>8. Risk Analysis and Risk Management</h2>
      <p>
        Plexura performs a comprehensive HIPAA risk analysis at least
        annually and after any material change to the environment.
        Identified risks are entered into a risk register, prioritized
        by likelihood and impact, and remediated through documented
        risk-management actions. Penetration testing is performed
        annually by a third-party provider; vulnerability scanning is
        run continuously against production infrastructure.
      </p>

      <h2>9. Breach Notification (45 C.F.R. Part 164, Subpart D)</h2>
      <p>
        If Plexura discovers a Breach (as defined at &sect; 164.402) of
        Unsecured PHI, it will:
      </p>
      <ul>
        <li>
          Notify the affected Covered Entity without unreasonable delay
          and in no case later than <strong>sixty (60) calendar
          days</strong> after discovery (the BAA may impose a shorter
          window);
        </li>
        <li>
          Provide the information required by &sect; 164.410, including
          the identification of each individual whose PHI has been or is
          reasonably believed to have been accessed, acquired, used, or
          disclosed during the Breach;
        </li>
        <li>
          Cooperate with the Covered Entity&rsquo;s investigation,
          mitigation, and notice obligations to individuals, the
          Department of Health &amp; Human Services, and (where
          applicable) the media; and
        </li>
        <li>
          Document the Breach and Plexura&rsquo;s response per HIPAA
          recordkeeping requirements.
        </li>
      </ul>
      <p>
        Reports of suspected Breach or other security incidents may be
        sent to{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a>{' '}
        24x7. Suspected Breach is escalated to the Privacy &amp; Security
        Officer immediately upon receipt.
      </p>

      <h2>10. Data Retention, Return, and Destruction</h2>
      <p>
        Upon termination of the BAA, Plexura will, if feasible, return
        or destroy all PHI it maintains in any form on behalf of the
        Covered Entity. Where return or destruction is not feasible
        (e.g., due to legal-hold or backup-immutability constraints),
        Plexura will extend the protections of the BAA to the retained
        PHI and limit further uses and disclosures to the purposes that
        make return or destruction infeasible. Audit-log records are
        retained for at least six (6) years per &sect;
        164.316(b)(2)(i).
      </p>

      <h2>11. State Law and Substance-Use Disorder Records (42 C.F.R. Part 2)</h2>
      <p>
        Where a Covered Entity is also subject to state laws more
        protective than HIPAA (e.g., California CMIA, New York Public
        Health Law &sect; 18, Texas HB 300), Plexura&rsquo;s safeguards
        do not displace the Covered Entity&rsquo;s obligation to comply
        with those laws. If your practice handles substance-use
        disorder records subject to 42 C.F.R. Part 2, please contact{' '}
        <a href="mailto:info@plexura.ai">info@plexura.ai</a> before
        uploading those records so that we can document the additional
        consent and disclosure restrictions.
      </p>

      <h2>12. Customer Responsibilities</h2>
      <p>
        Plexura&rsquo;s safeguards do not relieve the Covered Entity of
        its own HIPAA obligations. Customers remain responsible for:
      </p>
      <ul>
        <li>
          Obtaining and documenting valid, informed patient consent
          before recording sessions through the Service;
        </li>
        <li>
          Configuring access controls within their practice account
          to enforce minimum-necessary access among their own
          workforce;
        </li>
        <li>
          Reviewing and approving every AI-generated note before
          signing or pushing it to an EHR;
        </li>
        <li>
          Promptly reporting suspected breach or unauthorized access to
          Plexura at{' '}
          <a href="mailto:info@plexura.ai">info@plexura.ai</a>;
        </li>
        <li>
          Maintaining their own HIPAA Privacy Rule documentation,
          notice of privacy practices, and patient-rights fulfillment
          processes.
        </li>
      </ul>

      <h2>13. Reporting and Compliance Contacts</h2>
      <p>
        Plexura AI Scribe<br />
        Privacy &amp; Security Officer<br />
        Email: <a href="mailto:info@plexura.ai">info@plexura.ai</a>
      </p>
      <p>
        The same address handles HIPAA inquiries, security incident reports,
        and patient/individual rights requests. Suspected breach notifications
        are escalated to the Privacy &amp; Security Officer immediately upon
        receipt and acknowledged within one business hour.
      </p>
      <p>
        Patients who believe their privacy rights have been violated may
        also file a complaint directly with the U.S. Department of
        Health &amp; Human Services, Office for Civil Rights:{' '}
        <a href="https://www.hhs.gov/hipaa/filing-a-complaint" target="_blank" rel="noreferrer">
          https://www.hhs.gov/hipaa/filing-a-complaint
        </a>.
      </p>
    </LegalLayout>
  );
}
